import { FaberApi, JetpacPayApi } from 'api'
import { AxiosPromise } from 'axios'
import {
  CDRDataUsageResponseType,
  DataCDRDetailResponseType,
  DataCDRResponseType,
  DataCheckReponseType,
  EsimsInfo,
  EsimsInfoPaginated,
  EsimStatusType,
  ImsiType,
  RefreshNetworkType,
  SubscriptionData,
  UserPlansByICCIDs
} from 'types/api/faber/dataUsage.type'
import { GetAllCustomerInvoiceTransaction } from 'types/api/faber/payment.type'

class DataApi {
  /**
   * To manually rollover user
   */
  static executeManualRollover({
    timeStamp: timeStamp,
    iccid: iccid,
    faberToken: faberToken
  }: {
    timeStamp: Date
    iccid: string
    faberToken: string
  }) {
    return FaberApi.httpPost(
      `/rolloverv2/manual_${timeStamp}_data_rollover_v2`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      {
        rolloverDate: '',
        rolloverICCID: [iccid]
      }
    )
  }

  static getDataCDROverview(
    iccid: string,
    faberToken: string
  ): AxiosPromise<DataCDRResponseType> {
    return FaberApi.httpGet(
      `/cdr/package/group/${iccid}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getExternalDataCDROverview(
    iccid: string,
    faberToken: string
  ): AxiosPromise<DataCDRResponseType> {
    return FaberApi.httpGet(
      `/sim/ext/cdr/package/group/${iccid}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getUserSubscription(
    faberToken: string,
    userID: string
  ): AxiosPromise<SubscriptionData> {
    return JetpacPayApi.httpGet(
      `/SGP/ops/stripe/subscription/user/${userID}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static cancelUserSubscription(
    faberToken: string,
    userID: string,
    body: { reasons: string[] }
  ): AxiosPromise<SubscriptionData> {
    return JetpacPayApi.httpPut(
      `/SGP/ops/stripe/subscription/${userID}`,
      body,
      {
        Authorization: `Bearer ${faberToken}`
      }
    )
  }

  static getCustomerInvoiceList(
    faberToken: string,
    userID: string | ''
  ): AxiosPromise<GetAllCustomerInvoiceTransaction> {
    return JetpacPayApi.httpGet(
      `/SGP/ops/stripe/payment/invoice/${userID}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getExternalUserSubscription(
    userID: string,
    faberToken: string
  ): AxiosPromise<SubscriptionData> {
    return JetpacPayApi.httpGet(
      `/SGP/ext/stripe/subscription/user/${userID}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getDataCDRDetailedRecords(
    iccid: string,
    faberToken: string
  ): AxiosPromise<DataCDRDetailResponseType> {
    return FaberApi.httpGet(
      `/cdr/package/detail/${iccid}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getExternalDataCDRDetailedRecords(
    iccid: string,
    faberToken: string
  ): AxiosPromise<DataCDRDetailResponseType> {
    return FaberApi.httpGet(
      `/sim/ext/cdr/package/detail/${iccid}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getUserDataCheck(
    iccid: string,
    faberToken: string
  ): AxiosPromise<DataCheckReponseType> {
    return FaberApi.httpGet(
      `/cdr/data-check/${iccid}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getImsiData(
    faberToken: string,
    ICCID: string
  ): AxiosPromise<ImsiType> {
    return FaberApi.httpGet(
      `/location/latest/${ICCID}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getEsimStatus(
    faberToken: string,
    userID: string
  ): AxiosPromise<EsimStatusType> {
    return FaberApi.httpGet(
      `/sim/ops/esim/status/${userID}`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getUserEsimStatusByUserID(
    faberToken: string,
    userID: string
  ): AxiosPromise<EsimsInfo> {
    return FaberApi.httpGet(
      `sim/ops/multiesim/wallet/user/${userID}/status`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getAllUserEsimsByUserIDPaginated(
    limit: number,
    offset: number,
    faberToken: string,
    userID: string
  ): AxiosPromise<EsimsInfoPaginated> {
    return FaberApi.httpGet(
      `sim/ops/multiesim/wallet/user/${userID}?offset=${offset}&limit=${limit}&excludeStatus=TERMINATED`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getUserPlansByICCIDsByUserID(
    faberToken: string,
    userID: string
  ): AxiosPromise<UserPlansByICCIDs> {
    return FaberApi.httpGet(
      `sim/ops/multiesim/wallet/${userID}/userplans`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getExternalEsimStatus(
    faberToken: string,
    userID: string
  ): AxiosPromise<EsimStatusType> {
    return FaberApi.httpGet(
      `/sim/ext/esim/status/${userID}`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static refreshNetwork({
    userId: userId,
    iccid: iccid,
    faberToken: faberToken
  }: {
    userId: string
    iccid: string
    faberToken: string
  }): AxiosPromise<RefreshNetworkType> {
    return FaberApi.httpPost(
      `/sim/ops/esim/refresh/${userId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      {
        iccid: iccid
      }
    )
  }

  static externalRefreshNetwork({
    userId: userId,
    iccid: iccid,
    faberToken: faberToken
  }: {
    userId: string
    iccid: string
    faberToken: string
  }): AxiosPromise<RefreshNetworkType> {
    return FaberApi.httpPost(
      `/sim/ext/esim/refresh/${userId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      {
        iccid: iccid
      }
    )
  }

  static getCDRDataUsage(
    userId: string,
    faberToken: string
  ): AxiosPromise<CDRDataUsageResponseType> {
    return FaberApi.httpGet(
      `/sim/ops/cdr/${userId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getExternalCDRDataUsage(
    userId: string,
    faberToken: string
  ): AxiosPromise<CDRDataUsageResponseType> {
    return FaberApi.httpGet(
      `/sim/ext/cdr/${userId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }
}
export default DataApi
